import Color from '@arcgis/core/Color';
import GeoJSONLayer from '@arcgis/core/layers/GeoJSONLayer';
import LabelClass from '@arcgis/core/layers/support/LabelClass';
import MapView from '@arcgis/core/views/MapView';
import { TextLayer } from '@deck.gl/layers';
import { DEFAULT_MAP_SOURCE } from '@env';
import * as turf from '@turf/turf';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { SnGeoJSONLayer, SnInternalGeoJSONLayer } from '@lib/api/entities';
import { hexToRgbArray, hexToRgba, genFaFonts } from '@lib/common/helpers';
import { FsMap } from '@lib/geo/map';
import { GoogleMap } from 'libs/geo/google/map/src/lib/map';

import { FontAwesomeIconMapper } from '../markers';
import { FsSvgIconLayer } from './deckgl-layers';
import { FsDeckGLOverlay } from './deckgl-overlay';

export interface ISnFAIconConfig {
  fontFamily: string;
  fontWeight: string;
}

export interface LayerOptions {
  clickable: boolean;
  hoverable: boolean;
}

export class FsGeoJSONLayer {
  public overlay: GeoJSONLayer | google.maps.Data;
  public googleMarkers: google.maps.Marker[] = [];
  public esriLabelsId: string[] = [];
  public mapper = new FontAwesomeIconMapper();

  public labelClass: LabelClass;
  public labelMarker: google.maps.marker.AdvancedMarkerElement;

  private readonly _newClick$: Subject<any> = new Subject();
  private readonly _newHover$: Subject<any> = new Subject();
  private readonly _destroy$ = new Subject<void>();

  get newClick$(): Observable<any> {
    return this._newClick$.asObservable();
  }

  get newHover$(): Observable<any> {
    return this._newHover$.asObservable();
  }

  get defaultMapSource(): string {
    return DEFAULT_MAP_SOURCE;
  }

  constructor(
    public data: {
      url: string;
      layer: SnInternalGeoJSONLayer | SnGeoJSONLayer;
      map: FsMap;
      layerOptions: LayerOptions;
      deckglOverlay: FsDeckGLOverlay;
      label?: string;
    },
  ) {
    switch (this.defaultMapSource) {
      case 'esri':
        const symbol = {
          type: 'simple',
          symbol: {
            type: 'simple-fill',
            color: new Color(
              `${hexToRgba(data.layer.meta.fillColor, data.layer.meta.fillOpacity)}`,
            ),
            outline: {
              width: data.layer.meta.strokeWeight,
              color: new Color(data.layer.meta.strokeColor),
            },
          },
        };
        this.overlay = this._handleEsriLayer(this.data.url, symbol, this.data.layer);
        break;
      case 'google':
        this.overlay = this._handleGoogleLayer(this.data.url, this.data.layer);
        break;
    }

    this.data.deckglOverlay.newClick$.pipe(takeUntil(this._destroy$)).subscribe((info) => {
      if (info.object) {
        const data = this._formatGeoJsonClick(info.object);
        this._onClick(data);
      }
    });
  }

  public removeMarkers(): void {
    this.googleMarkers.forEach((marker) => {
      marker.setMap(null);
    });
    this.esriLabelsId.forEach((label) => {
      this.data.deckglOverlay.removeLayer(label);
    });
  }

  public remove(): void {
    this.data.map.removeImageLayer(this.overlay);
  }

  private _handleEsriLayer(url: string, renderer: any, layer): GeoJSONLayer {
    const geoJsonLayer = new GeoJSONLayer({
      renderer,
      url,
    });

    geoJsonLayer.on('layerview-create', (e) => {
      geoJsonLayer.queryFeatures().then((result) => {
        this._genEsriLabels(result.features, layer);
      });
      e.view.on('click', (event) => {
        (e.view as MapView).hitTest(event).then((response: any) => {
          if (response.results.length) {
            const graphic = response.results.filter((res) => {
              return res.layer.type === 'geojson';
            })[0]?.graphic;
            if (graphic && this.data.layerOptions.clickable) {
              this._onClick(graphic);
            }
          }
        });
      });

      e.view.on('pointer-move', (event) => {
        const screenPoint = {
          x: event.x,
          y: event.y,
        };
        (e.view as MapView).hitTest(screenPoint).then((response: any) => {
          if (response.results.length) {
            const graphic = response.results.filter((res) => {
              return res.layer?.type === 'geojson';
            })[0]?.graphic;
            if (this.data.layerOptions.hoverable) {
              this._onHover(graphic || null);
            }
          }
        });
      });
    });

    return geoJsonLayer;
  }

  private _handleGoogleLayer(
    url: string,
    layer: SnGeoJSONLayer | SnInternalGeoJSONLayer,
  ): google.maps.Data {
    const dataLayer = new google.maps.Data();
    dataLayer.loadGeoJson(url);

    this._setGeojsonStyles(layer, dataLayer);

    return dataLayer;
  }

  private _setGeojsonStyles(
    layer: SnGeoJSONLayer | SnInternalGeoJSONLayer,
    dataLayer: google.maps.Data,
  ): void {
    dataLayer.setStyle((feature: any) => {
      this._genGoogleLabels(feature, layer);
      return {
        visible: true,
        clickable: this.data.layerOptions.clickable ? true : false,
        strokeWeight: feature.getProperty('strokeWeight')
          ? feature.getProperty('strokeWeight')
          : layer.meta?.strokeWeight,
        strokeOpacity: feature.getProperty('strokeOpacity')
          ? feature.getProperty('strokeOpacity')
          : layer.meta?.strokeOpacity,
        strokeColor: feature.getProperty('strokeColor')
          ? feature.getProperty('strokeColor')
          : layer.meta?.strokeColor,
        fillOpacity: feature.getProperty('fillOpacity')
          ? feature.getProperty('fillOpacity')
          : layer.meta?.fillOpacity,
        fillColor: feature.getProperty('fillColor')
          ? feature.getProperty('fillColor')
          : layer.meta?.fillColor,
        icon: layer.meta?.iconUnicode
          ? {
              path: google.maps.SymbolPath.CIRCLE,
              fillOpacity: 0,
              strokeWeight: 0,
            }
          : feature.getProperty('icon')
          ? feature.getProperty('icon')
          : layer?.icon,
      };
    });
  }

  private _genGoogleLabels(
    feature: any,
    layer: SnGeoJSONLayer | SnInternalGeoJSONLayer,
  ): google.maps.Marker {
    let labelMarker: google.maps.Marker;

    if (feature.getGeometry().getType() === 'Point' && layer.meta.fontSet) {
      let position;
      const fontConfig: ISnFAIconConfig = genFaFonts(layer);
      feature.getGeometry().forEachLatLng((latlon) => {
        position = latlon;
      });
      labelMarker = new google.maps.Marker({
        position,
        map: (this.data.map as unknown as GoogleMap).mapInstance,
        draggable: false,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          fillOpacity: layer.meta.iconFillOpacity,
          strokeWeight: layer.meta.iconStrokeWeight,
          strokeOpacity: layer.meta.iconStrokeOpacity,
          strokeColor: layer.meta.iconStrokeColor,
          fillColor: layer.meta.iconFillColor,
          scale: layer.meta.iconScale,
        },
        label: {
          fontFamily: fontConfig.fontFamily,
          fontWeight: fontConfig.fontWeight,
          // tslint:disable-next-line:no-eval
          text: eval(`'\\u${layer.meta.iconUnicode}'`),
          color: layer.meta.iconColor,
          fontSize: layer.meta.iconFontSize,
        },
      });
      labelMarker.addListener('click', (res) => {
        feature.toGeoJson((geo) => {
          this._onClick({
            geometry: {
              ...geo.geometry,
            },
            properties: {
              ...geo.properties,
            },
            type: geo.geometry.type,
          });
        });
      });
    } else {
      if (!layer.meta.showLabel) {
        return;
      }
      const polygonBounds = new google.maps.LatLngBounds();
      let labelCoords;
      feature.getGeometry().forEachLatLng((coords) => {
        polygonBounds.extend(coords);
      });

      if (feature.getProperty('labelPosition')) {
        const point = feature.getProperty('labelPosition');
        labelCoords = new google.maps.LatLng(point[0], point[1]);
      } else {
        labelCoords = polygonBounds.getCenter();
      }

      labelMarker = new google.maps.Marker({
        position: labelCoords,
        map: (this.data.map as unknown as GoogleMap).mapInstance,
        draggable: false,
        label: {
          text: feature.getProperty('label')
            ? feature.getProperty('label').toString()
            : feature.getProperty(layer.meta.labelField)?.toString(),
          color: '#fff',
          fontSize: '12px',
        },
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          fillOpacity: 0,
          strokeWeight: 0,
        },
      });
    }
    this.googleMarkers.push(labelMarker);
    return labelMarker;
  }

  private _genEsriLabels(
    features: __esri.Graphic[],
    layer: SnGeoJSONLayer | SnInternalGeoJSONLayer,
  ): any {
    const featureData = [];
    let type;
    features.forEach((feature) => {
      if (feature.geometry.type === 'point' && layer.meta.fontSet) {
        type = 'point';
        const fontConfig: ISnFAIconConfig = genFaFonts(layer);
        const point = turf.point([
          (feature.geometry as any).longitude,
          (feature.geometry as any).latitude,
        ]);
        const bbox = turf.bbox(point);
        const bboxPolygon = turf.bboxPolygon(bbox);
        const coords = turf.center(bboxPolygon);

        featureData.push({
          coordinates: coords,
          feature,
          icon: {
            text: layer.meta.iconUnicode,
            color: layer.meta.iconColor,
          },
          style: {
            fillOpacity: layer.meta.iconFillOpacity,
            strokeWeight: layer.meta.iconStrokeWeight,
            strokeOpacity: layer.meta.iconStrokeOpacity,
            strokeColor: layer.meta.iconStrokeColor,
            fillColor: layer.meta.iconFillColor,
            scale: layer.meta.iconScale,
            fontConfig,
          },
        });
      } else {
        if (!layer.meta.showLabel) {
          return;
        }
        let labelCoords;
        type = 'polygon';
        const point = turf.point([
          feature.geometry.extent.center.longitude,
          feature.geometry.extent.center.latitude,
        ]);
        const bbox = turf.bbox(point);
        const bboxPolygon = turf.bboxPolygon(bbox);
        if (feature.getAttribute('labelPosition')) {
          const point = feature.getAttribute('labelPosition');
          labelCoords = turf.point(point[0], point[1]);
        } else {
          labelCoords = turf.center(bboxPolygon);
        }
        featureData.push({
          coordinates: labelCoords,
          feature: feature.attributes,
        });
      }
    });

    if (type === 'point') {
      const esriIconLayer = new FsSvgIconLayer<any>(
        {
          id: `esri-icons-${crypto.randomUUID()}`,
          data: featureData,
          getIcon: (d) => ({
            url: `/assets/fontawesome/SVG/${d.style.fontConfig.fontStyle}/${this.mapper.getIconName(
              d.icon.text,
            )}.svg`,
            width: d.style.scale ? d.style.scale : 24,
            height: d.style.scale ? d.style.scale : 24,
            mask: true,
          }),
          getPosition: (d) => d.coordinates.geometry.coordinates,
          getSize: (d) => (d.style.scale ? d.style.scale : 24),
          getLineColor: (d) => hexToRgbArray(d.style.iconStrokeColor),
          getFillColor: (d) => hexToRgbArray(d.style.fillColor),
          getLineWidth: (d) => d.style.strokeWeight,
          onClick: (d) => {
            this._onClick(d);
          },
          getTooltip: (d) => {},
        },
        'FsGeoJSONIconLayer',
      );
      this.data.deckglOverlay.addLayer(esriIconLayer);
      this.esriLabelsId.push(esriIconLayer.id);
    } else {
      const esriLabelText = new TextLayer({
        id: `esri-labels-${crypto.randomUUID()}`,
        data: featureData,
        fontFamily: 'IBM Plex Sans',
        getPosition: (d) => d.coordinates.geometry.coordinates,
        getText: (d) => {
          return `${d.feature[this.data.label]}`;
        },
        getColor: () => [255, 255, 255],
        getTooltip: () => null,
        getSize: 10,
        getAngle: 0,
      });
      this.data.deckglOverlay.addLayer(esriLabelText);
      this.esriLabelsId.push(esriLabelText.id);
    }

    this.data.map.addLayer(this.data.deckglOverlay.overlay);
    return;
  }

  private _onClick(object: any): void {
    this._newClick$.next(object);
  }

  private _onHover(object: any): void {
    this._newHover$.next(object);
  }

  private _formatGeoJsonClick(info): any {
    switch (this.defaultMapSource) {
      case 'esri':
        return {
          geometry: {
            ...info.coordinates.geometry,
          },
          properties: {
            ...info.feature.attributes,
          },
          type: info.coordinates.geometry.type,
        };
      case 'google':
        break;
    }
  }
}
